import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Stethoscope, Users, Send } from "lucide-react";

export default function SharedView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState({ author: "", text: "" });
  const [posting, setPosting] = useState(false);

  const load = () => api.get(`/share/view/${token}`).then((r) => setData(r.data)).catch((e) => setErr("This link is invalid or has been revoked."));
  useEffect(() => {
    load();
    // Clinician-only trend
    api.get(`/share/view/${token}/trends`).then((r) => setTrend(r.data)).catch(() => {});
  /* eslint-disable-next-line */
  }, [token]);

  const postNote = async () => {
    if (!note.author.trim() || !note.text.trim()) return;
    setPosting(true);
    try {
      await api.post(`/share/view/${token}/notes`, {
        author: note.author,
        role: data?.invite?.role || "carer",
        text: note.text,
      });
      setNote({ author: note.author, text: "" });
      toast.success("Note posted");
      await load();
    } catch (e) {
      toast.error("Could not post note");
    } finally {
      setPosting(false);
    }
  };

  if (err) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md w-full text-center" data-testid="shared-error">
        <ShieldCheck className="w-10 h-10 mx-auto text-slate-300" />
        <h1 className="font-display text-xl font-bold text-slate-900 mt-3">Link unavailable</h1>
        <p className="text-sm text-slate-500 mt-1">{err}</p>
      </div>
    </div>;
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="shared-loading">Loading…</div>;

  const { user, plan, invite, notes } = data;
  const RoleIcon = invite.role === "clinician" ? Stethoscope : Users;

  const sections = [
    { title: "Warning signs", items: plan.warning_signs || [], type: "list" },
    { title: "Internal coping strategies", items: plan.coping_strategies || [], type: "list" },
    { title: "Calming activities", items: plan.calming_activities || [], type: "list" },
    { title: "People & places for distraction", items: plan.social_distractions || [], type: "contacts" },
    { title: "People to talk to", items: plan.trusted_supports || [], type: "contacts" },
    { title: "Professional support", items: plan.professional_supports || [], type: "contacts" },
    { title: "Reasons for living", items: plan.reasons_for_living || [], type: "list" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="shared-view-page">
      <div className="max-w-3xl mx-auto space-y-5 fade-up">
        <header className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${invite.role === "clinician" ? "bg-amber-50 text-amber-800" : "bg-teal-50 text-teal-800"}`}>
              <RoleIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500">Shared {invite.role} view</div>
              <h1 className="font-display text-2xl font-bold text-slate-900">{user.name}'s safety plan</h1>
            </div>
          </div>
          <p className="text-sm text-slate-500">You are signed in as <strong>{invite.name}</strong>. This is a read-only view; the plan owner controls and can revoke access at any time. {user.mode === "inpatient" && <span className="inline-flex ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-900">Inpatient</span>}</p>
        </header>

        {trend && trend.severity !== "stable" && (
          <div className={`rounded-2xl border p-4 ${trend.severity === "high" ? "bg-red-50 border-red-200 text-red-900" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="shared-trend-alert">
            <div className="font-semibold">{trend.severity === "high" ? "Clinical alert · significant deterioration" : "Trend alert · worsening over the past week"}</div>
            <div className="text-sm mt-1">
              Anxiety Δ {trend.anxiety_delta?.toFixed(1)} · Self-harm urge Δ {trend.urge_delta?.toFixed(1)} · Distress Δ {trend.distress_delta?.toFixed(1)} · Mood Δ {trend.mood_delta?.toFixed(1)} (last 3d vs prior 4d).
            </div>
          </div>
        )}

        {sections.map((s) => (
          <div key={s.title} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid={`shared-section-${s.title.replace(/\s/g, "-").toLowerCase()}`}>
            <h2 className="font-display text-lg font-bold text-slate-900">{s.title}</h2>
            {s.items.length === 0 ? (
              <p className="text-sm text-slate-400 mt-1">Nothing recorded yet.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {s.items.map((it, i) => (
                  <li key={i} className="bg-slate-50 rounded-xl px-3 py-2 text-sm text-slate-800">
                    {s.type === "list" ? it : <><strong>{it.name}</strong> · {it.phone}{it.relationship ? ` · ${it.relationship}` : ""}</>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="shared-notes">
          <h2 className="font-display text-lg font-bold text-slate-900">Notes from {invite.role === "clinician" ? "the care team" : "carers and family"}</h2>
          <div className="mt-3 space-y-2">
            {notes.length === 0 && <div className="text-sm text-slate-400">No notes yet.</div>}
            {notes.map((n) => (
              <div key={n.id} className="border border-slate-200 rounded-xl p-3" data-testid={`note-${n.id}`}>
                <div className="text-xs text-slate-500">{n.author} · <span className={n.role === "clinician" ? "text-amber-700" : "text-teal-700"}>{n.role}</span> · {new Date(n.created_at).toLocaleString("en-GB")}</div>
                <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{n.text}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-2 border-t border-slate-200 pt-4">
            <input value={note.author} onChange={(e) => setNote({ ...note, author: e.target.value })} placeholder="Your name" className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500" data-testid="note-author" />
            <textarea value={note.text} onChange={(e) => setNote({ ...note, text: e.target.value })} rows={3} placeholder={invite.role === "clinician" ? "Clinical note / intervention (e.g. 'CMHT contacted, follow-up Friday')" : "How are you supporting them? Any concerns?"} className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500" data-testid="note-text" />
            <button data-testid="note-submit" disabled={posting} onClick={postNote} className="bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-1">
              <Send className="w-4 h-4" /> Post note
            </button>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
          If you are concerned about {user.name}'s immediate safety please call <strong>999</strong> or NHS 111.
        </div>
      </div>
    </div>
  );
}
